import React, { useCallback, useEffect, useRef, useState } from 'react';
import Logo from "../../assets/images/Logo.svg";
import FvBankLogo from "../../assets/images/LogoNew.svg";
import { doGET, doPUT } from '../../util/HttpUtil';
import { INVALID_VERIFY_LINK_MESSAGE } from '../../util/authResume';
import { brokerLogoSrc } from '../../util/brokerAuth';
import './UboDashboard.scss'; // Make sure to create and import the corresponding CSS file
import OndatoVerification from './components/ondato/OndatoVerification';
import PreUboDashboard from './PreUboDashboard';
import Skeleton from 'react-loading-skeleton';
import FormContainerV2 from './forms/BusinessV2/FormContainerV2';
import SelfDeclarationV2 from './forms/BusinessV2/SelfDeclarationV2';
import { createFieldDefinitionMap, resolveFieldsForType, V2_TYPE, WIZARD, WIZARD_NUM } from './forms/BusinessV2/helperV2';
import { hydrateBusinessV2PayloadFromApi } from './forms/BusinessV2/compatibilityV2';
import { MANAGED_ACCOUNT_TNC_DOCS, areAllManagedAccountTncAccepted, toggleManagedAccountTncDoc } from './forms/BusinessV2/managedAccountTnc';
import IndividualManagedAccountTerms from './forms/components/IndividualLite/IndividualManagedAccountTerms';


// Self-Declaration fields the broker-flow autosave must persist: the two
// acknowledgement checkboxes plus the applicant's signature and name.
const TERMS_PERSIST_FIELDS = [
  "acceptDelarationAggrement",
  "tnc",
  "signature",
  "signerFirstName",
  "signerMiddleName",
  "signerLastName",
];

const getStatusName = (status) => {
  switch (status) {
    case 0:
      return 'KYC Pending';
    case 1:
      return 'KYC Done';
    case 2:
      return 'KYC Pending'; 
    case 3:
      return 'KYC Done'; 
    case 4:
      return 'KYC Rejected'; 
    default:
      return 'KYC Pending'; 
  }
};


export const getColorForStatusViaString = (sts) => {
  switch (sts) {
    case "Draft":
      return "#6C757D";   
    case "Submitted":
      return "#6C757D";   
    case "Ask More Info":
      return "#ccb363";  
    case "Approved":
      return "#155724";  
    case "Rejected":
      return "#721c24";  
    case "KYC Rejected":
      return "#721c24"; 
    case "KYC Done":
      return "#155724"; 
    default:
      return "#6C757D";   
  }
};

export const getBackgroundColorForStatusViaString = (sts) => {
  switch (sts) {
    case "Draft":
      return "#F2F2F2";  
    case "Submitted":
      return "#F2F2F2";  
    case "Ask For Info":
      return "#F2F2F2";   
    case "Approved":
      return "#d4edda";   
    case "Rejected":
      return "#f8d7da";  
    case "KYC Rejected":
      return "#f8d7da";
    case "KYC Done":
      return "#d4edda"; 
    default:
      return "#F2F2F2";  
  }
};


// Skeleton Loader Components
const SkeletonLoader = () => (
  <div className="fv_ubo_skeleton w-100">
    <Skeleton height={30}/>
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
    <Skeleton height={30} />
  </div>
);

const BrokerOnboardingSkeletonLoader = () => (
  <FormContainerV2
    title="Individual Verification"
    isLoading={false}
    applicationForm={null}
    hideLogout
  >
    <div className="form-content-body-content flex-1 fv_ubo_onboarding_skeleton" aria-label="Loading individual verification">
      <div className="fv_ubo_onboarding_skeleton__partner">
        <Skeleton height={38} width={180} baseColor="#f0f0f0" highlightColor="#fafafa" />
        <Skeleton height={16} width={130} baseColor="#f0f0f0" highlightColor="#fafafa" />
      </div>
      <div className="fv_ubo_onboarding_skeleton__intro">
        <Skeleton height={28} width="42%" baseColor="#eeeeee" highlightColor="#fafafa" />
        <Skeleton height={16} width="82%" baseColor="#eeeeee" highlightColor="#fafafa" />
        <Skeleton height={16} width="64%" baseColor="#eeeeee" highlightColor="#fafafa" />
      </div>
      <div className="fv_ubo_onboarding_skeleton__panel">
        {[72, 46, 46, 46, 64, 46].map((height, index) => (
          <Skeleton key={index} height={height} baseColor="#eeeeee" highlightColor="#fafafa" />
        ))}
      </div>
      <div className="fv_ubo_onboarding_skeleton__actions">
        <Skeleton height={40} width={160} baseColor="#111111" highlightColor="#333333" />
      </div>
    </div>
  </FormContainerV2>
);

const areBusinessTermsAccepted = (data = {}, fields = {}) => {
  const requiresTermsAcceptance = Boolean(fields?.acceptDelarationAggrement);
  const requiresBeneficialOwnerCertification = Boolean(fields?.tnc);

  return (!requiresTermsAcceptance || data?.acceptDelarationAggrement === true)
    && (!requiresBeneficialOwnerCertification || data?.tnc === true);
};

const getTermsAutoSaveMessage = (status) => {
  switch (status) {
    case "pending":
      return "Autosave pending";
    case "saving":
      return "Saving changes";
    case "saved":
      return "Saved automatically";
    case "error":
      return "Autosave failed. Use Next to retry.";
    default:
      return "";
  }
};

const UboDashboard = ({ brokerCompleteApplication = false } = {}) => {
  const [step, setStep] = useState("main");  
  const [uboData, setUboData] = useState(null);
  const [ondatoData, setOndatoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [usernameOnly, setUsernameOnly] = useState(false);
  const [applicationForm, setApplicationForm] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [businessFields, setBusinessFields] = useState({});
  const [savingTerms, setSavingTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [termsHasError, setTermsHasError] = useState(false);
  const [termsErrors, setTermsErrors] = useState({});
  const [termsStepComplete, setTermsStepComplete] = useState(false);
  const [termsAutoSaveStatus, setTermsAutoSaveStatus] = useState("idle");
  const termsAutoSaveTimerRef = useRef(null);
  const businessDataRef = useRef(null);
  // Serializes terms field-saves so each uses the latest server version.
  const termsSaveChainRef = useRef(Promise.resolve());
  // Individual Plus broker completion (D3): per-doc TnC acceptances + signature, owned here,
  // persisted via /api/individual-lite/update-field. Separate from the business terms state so
  // the business completion flow is byte-identical.
  const [individualAcceptedDocs, setIndividualAcceptedDocs] = useState([]);
  const [individualSign, setIndividualSign] = useState("");
  const [individualTermsComplete, setIndividualTermsComplete] = useState(false);
  const [savingIndividualTerms, setSavingIndividualTerms] = useState(false);
  const [individualTermsShowError, setIndividualTermsShowError] = useState(false);
  const getContainerClassName = (extraClassName = "") => (
    ["fv_ubo_container", brokerCompleteApplication ? "fv_ubo_broker_dashboard" : "", extraClassName]
      .filter(Boolean)
      .join(" ")
  );

  const fetchBusinessTermsData = async (applicationId, applicationType) => {
    const [businessResponse, fieldsResponse] = await Promise.all([
      doGET(`/api/business/detail?id=${applicationId}`),
      doGET('/api/cyclos/fields-json?group=Business'),
    ]);
    const hydratedBusiness = hydrateBusinessV2PayloadFromApi(businessResponse?.data || {});
    const resolvedType = Number(applicationType || hydratedBusiness?.type || V2_TYPE.PLUS);
    const resolvedFields = resolveFieldsForType(fieldsResponse?.data?.fields || [], resolvedType);
    const fieldDefinitionMap = createFieldDefinitionMap(resolvedFields);
    businessDataRef.current = hydratedBusiness;
    setBusinessData(hydratedBusiness);
    setBusinessFields(fieldDefinitionMap);
    setTermsStepComplete(areBusinessTermsAccepted(hydratedBusiness, fieldDefinitionMap));
  };

  const fetchUboData = async () => {
    const ubo_id = localStorage.getItem('ubo_id');
    if (!ubo_id) {
      setError(new Error(INVALID_VERIFY_LINK_MESSAGE));
      setLoading(false);
      return;
    }

    try {
      const response = await doGET(`/api/ubo/detail?_id=${ubo_id}`);
      const application_id = response?.data?.application_id

      const applicationResponse = await doGET(`/api/application-form/detail?id=${application_id}&withExtension=true`)

      if (response.status === 200) {
        const nextApplicationForm = applicationResponse?.data ?? null;
        setApplicationForm(nextApplicationForm);
        const isEddApplication = nextApplicationForm?.eDDApplication === true;
        const formType = nextApplicationForm?.formType;
        const isBusinessBusinessApplication = ["BUSINESS_PLUS", "BUSINESS_PRIME", "BUSINESS"].includes(formType);
        const isMissingAEId = !response?.data?.aEId;
        const isAuthorizedUser = response?.data?.isAuthorizedUser === true;
        const shouldCollectUsernameOnly = isEddApplication && isBusinessBusinessApplication && isMissingAEId && isAuthorizedUser;
        const isBrokerCompleteApplication = brokerCompleteApplication && Boolean(nextApplicationForm?.brokerId);
        const isSponsoredPrimaryUser = Boolean(nextApplicationForm?.brokerId) && response?.data?.isPrimaryUser === true;

        setUsernameOnly(shouldCollectUsernameOnly);
        if (isSponsoredPrimaryUser) {
          await fetchBusinessTermsData(application_id, nextApplicationForm?.type);
        } else {
          setBusinessData(null);
          setBusinessFields({});
          setTermsStepComplete(false);
        }

        if (isBrokerCompleteApplication) {
          setStep("main");
        } else if ((response?.data?.isAuthorizedUser && !isEddApplication) || shouldCollectUsernameOnly) {
          setStep("verify");
        }
        setUboData(response.data);

        if (isBrokerCompleteApplication && formType === "INDIVIDUAL_PLUS") {
          try {
            const ilDetail = await doGET(`/api/individual-lite/detail?id=${application_id}`);
            const il = ilDetail?.data || {};
            const accepted = Array.isArray(il.managedAccountTncAcceptedDocs) ? il.managedAccountTncAcceptedDocs : [];
            setIndividualAcceptedDocs(accepted);
            setIndividualSign(il.customerSign || "");
            setIndividualTermsComplete(Boolean(il.customerSign) && areAllManagedAccountTncAccepted(accepted));
          } catch (e) {
            // ignore — fall back to showing the Terms step
          }
        }

        const ondatoResponse = await doGET(`/api/ondato/grid?form_id=${response.data?.application_id}&ubo_id=${ubo_id}&sortBy=seq&sortAsc=false&rows=-1`);
        setOndatoData(ondatoResponse?.data?.rows[0])
      }
    } catch (err) {
      setError(err);
      // showError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUboData();
  }, []);

  useEffect(() => {
    businessDataRef.current = businessData;
  }, [businessData]);

  useEffect(() => () => {
    if (termsAutoSaveTimerRef.current) {
      clearTimeout(termsAutoSaveTimerRef.current);
    }
  }, []);

  const handleVerifyKYC = () => {
    window.location.reload()
  };

  // Sponsored (broker) applications: the primary user must personally accept the
  // Terms & Conditions and certify the Certificate of Beneficial Owners before
  // KYC — the broker admin cannot accept on their behalf.
  // Broker logo moved from a plain `brokerLogo` string to a `brokerLogoAttachment`
  // Attachment object; resolve a displayable URL from either (legacy fallback).
  const brokerLogoUrl = brokerLogoSrc(applicationForm);
  const isSponsoredApplication = Boolean(applicationForm?.brokerId)
    || Boolean(brokerLogoUrl);
  const requiresTermsAcceptance = Boolean(businessFields?.acceptDelarationAggrement);
  const requiresBeneficialOwnerCertification = Boolean(businessFields?.tnc);
  const termsAcceptanceRequired = requiresTermsAcceptance || requiresBeneficialOwnerCertification;
  const needsTermsAcceptance = isSponsoredApplication
    && uboData?.isPrimaryUser === true
    && termsAcceptanceRequired
    && !termsStepComplete;

  // Individual Plus broker completion (D3): the single client subject must accept the 7
  // managed-account docs + sign before KYC. Gated by formType, so business is unaffected
  // (for business, requiresTermsAcceptance/businessFields drive needsTermsAcceptance above).
  // NOTE: unlike business UBOs, a standalone Individual record has no `isPrimaryUser` flag —
  // there is exactly one subject (the completion-link token is their ubo_id), so we must NOT
  // gate on isPrimaryUser (doing so skipped the Terms step straight to KYC).
  const isIndividualPlus = applicationForm?.formType === "INDIVIDUAL_PLUS";
  const needsIndividualTermsAcceptance = isIndividualPlus
    && isSponsoredApplication
    && !individualTermsComplete;

  const handleToggleIndividualTncDoc = (file, checked) => {
    setIndividualAcceptedDocs((current) => toggleManagedAccountTncDoc(current, file, checked));
  };

  const submitIndividualTerms = async () => {
    if (!areAllManagedAccountTncAccepted(individualAcceptedDocs) || !individualSign) {
      setIndividualTermsShowError(true);
      return;
    }
    const recordId = uboData?.application_id || applicationForm?._id;
    if (!recordId) {
      setTermsError("Could not resolve the application to save your acceptance.");
      return;
    }
    setSavingIndividualTerms(true);
    setTermsError("");
    try {
      // Fetch the current version for the optimistic-locked update-field write (T-B8).
      const detail = await doGET(`/api/individual-lite/detail?id=${recordId}`);
      const version = detail?.data?.version ?? 0;
      const response = await doPUT("/api/individual-lite/update-field", {
        _id: recordId,
        version,
        fieldValueMap: {
          customerSign: individualSign,
          managedAccountTncAcceptedDocs: individualAcceptedDocs,
        },
      });
      if (response?.status === 200) {
        setIndividualTermsComplete(true);
      } else {
        setTermsError("Could not save your acceptance. Please try again.");
      }
    } catch (e) {
      setTermsError(typeof e === "string" ? e : (e?.message || "Could not save your acceptance. Please try again."));
    } finally {
      setSavingIndividualTerms(false);
    }
  };

  // Field-level autosave, mirroring the Business V2 forms: only the changed terms
  // fields are sent to /api/business/update-field as a { fieldValueMap }, and the
  // server's returned `version` is folded back in for optimistic-lock safety.
  // Saves are serialized on a promise chain so each read uses the latest version
  // (no DirtyUpdate races between the debounced autosave and the explicit submit).
  const persistBusinessTerms = useCallback(async (sourceData = {}, { markComplete = false } = {}) => {
    const _id = sourceData?._id || businessDataRef.current?._id || applicationForm?._id;
    if (!_id) return null;

    const fieldValueMap = {};
    // Signature + name autosave continuously; the Beneficial Owner Certification
    // (`tnc`) is committed only on the explicit Next/submit (markComplete).
    const fieldsToPersist = markComplete
      ? TERMS_PERSIST_FIELDS
      : TERMS_PERSIST_FIELDS.filter((field) => field !== "tnc");
    fieldsToPersist.forEach((field) => {
      const value = sourceData?.[field] !== undefined ? sourceData[field] : businessDataRef.current?.[field];
      if (value !== undefined) fieldValueMap[field] = value;
    });
    if (!Object.keys(fieldValueMap).length) {
      if (markComplete) setTermsStepComplete(true);
      return null;
    }

    const run = async () => {
      // Read the freshest version at execution time (a prior chained save may
      // have just bumped it), and never go backwards on version.
      const version = Number(businessDataRef.current?.version ?? sourceData?.version ?? 0);
      const response = await doPUT('/api/business/update-field', { _id, version, fieldValueMap });
      const nextVersion = response?.data?.version;
      if (nextVersion !== undefined && nextVersion !== null) {
        businessDataRef.current = { ...(businessDataRef.current || {}), ...fieldValueMap, version: nextVersion };
        setBusinessData((current) => ({ ...(current || {}), ...fieldValueMap, version: nextVersion }));
      }
      if (markComplete) setTermsStepComplete(true);
      return response;
    };

    const next = termsSaveChainRef.current.then(run, run);
    termsSaveChainRef.current = next.catch(() => {});
    return next;
  }, [applicationForm]);

  const scheduleTermsAutoSave = useCallback((nextBusinessData) => {
    if (!brokerCompleteApplication || (!nextBusinessData?._id && !applicationForm?._id)) return;

    if (termsAutoSaveTimerRef.current) {
      clearTimeout(termsAutoSaveTimerRef.current);
    }

    setTermsError("");
    setTermsAutoSaveStatus("pending");
    termsAutoSaveTimerRef.current = setTimeout(async () => {
      setTermsAutoSaveStatus("saving");
      try {
        await persistBusinessTerms(nextBusinessData);
        setTermsAutoSaveStatus("saved");
      } catch (err) {
        setTermsAutoSaveStatus("error");
      }
    }, 700);
  }, [applicationForm, brokerCompleteApplication, persistBusinessTerms]);

  const handleBusinessTermsChange = useCallback((fieldPathOrData, value, nextData) => {
    const current = businessDataRef.current || businessData || {};
    const candidate = typeof fieldPathOrData === "string"
      ? (nextData || {})
      : (typeof fieldPathOrData === "function" ? fieldPathOrData(current) : fieldPathOrData);
    const nextBusinessData = {
      ...current,
      ...(candidate || {}),
      _id: current?._id || candidate?._id || applicationForm?._id,
      type: current?.type || candidate?.type || applicationForm?.type || V2_TYPE.PLUS,
      formType: current?.formType || candidate?.formType || applicationForm?.formType,
      brokerId: current?.brokerId || candidate?.brokerId || applicationForm?.brokerId,
      brokerReferenceId: current?.brokerReferenceId || candidate?.brokerReferenceId || applicationForm?.brokerReferenceId,
      createdByBrokerAdminId: current?.createdByBrokerAdminId || candidate?.createdByBrokerAdminId || applicationForm?.createdByBrokerAdminId,
    };

    if (typeof fieldPathOrData === "string") {
      nextBusinessData[fieldPathOrData] = value;
      ["acceptDelarationAggrement", "tnc"].forEach((field) => {
        if (field !== fieldPathOrData && current?.[field] === true && candidate?.[field] !== true) {
          nextBusinessData[field] = true;
        }
      });
    }

    businessDataRef.current = nextBusinessData;
    setBusinessData(nextBusinessData);
    scheduleTermsAutoSave(nextBusinessData);
  }, [applicationForm, businessData, scheduleTermsAutoSave]);

  const submitTerms = async () => {
    const latestBusinessData = businessDataRef.current || businessData || {};
    if (!latestBusinessData?._id && !applicationForm?._id) return;
    if (termsAutoSaveTimerRef.current) {
      clearTimeout(termsAutoSaveTimerRef.current);
      termsAutoSaveTimerRef.current = null;
    }
    setSavingTerms(true);
    setTermsAutoSaveStatus("saving");
    setTermsError("");
    try {
      await persistBusinessTerms(latestBusinessData, { markComplete: true });
      setTermsAutoSaveStatus("saved");
      setStep("main");
    } catch (err) {
      setTermsError(err?.message || "Unable to record your acceptance. Please try again.");
      setTermsAutoSaveStatus("error");
    } finally {
      setSavingTerms(false);
    }
  };

  const handleShowQRCode = (show) => {
    setShowQRCode(show);
  };


  const handleVerificationSuccess = () => {
    setStep("main");
  };


  if (loading) {
    if (brokerCompleteApplication) {
      return <BrokerOnboardingSkeletonLoader />;
    }

    return (
      <div className={getContainerClassName()}>
        <SkeletonLoader />
      </div>
    );
  }

  if (error) {
    if (brokerCompleteApplication) {
      return (
        <FormContainerV2
          title="Individual Verification"
          isLoading={false}
          applicationForm={null}
          hideLogout
        >
          <div className="form-content-body-content flex-1">
            <div className="fv_ubo_error">Error: {error.message}</div>
          </div>
        </FormContainerV2>
      );
    }

    return (
      <div className={getContainerClassName()}>
        <div className="fv_ubo_error">Error: {error.message}</div>
      </div>
    );
  }

  if (needsIndividualTermsAcceptance) {
    return (
      <FormContainerV2
        form_id={applicationForm?._id || uboData?.application_id}
        title="Terms & Signature"
        isLoading={false}
        applicationForm={null}
        formData={uboData}
        hideLogout
        hideLogo={Boolean(brokerLogoUrl || applicationForm?.brokerName)}
      >
        <div className="form-content-body-content flex-1 fv_ubo_terms_onboarding_content">
          {brokerLogoUrl || applicationForm?.brokerName ? (
            <div className="fv_ubo_terms_partner">
              <img src={FvBankLogo} alt="FV Bank" className="fv_ubo_terms_partner_fv" />
              <span className="fv_ubo_terms_partner_x" aria-hidden="true">×</span>
              {brokerLogoUrl ? (
                <img
                  src={brokerLogoUrl}
                  alt={applicationForm.brokerName || "Broker"}
                  className="fv_ubo_terms_partner_logo"
                  onError={(event) => { event.currentTarget.style.display = "none"; }}
                />
              ) : null}
              {applicationForm?.brokerName ? (
                <span className="fv_ubo_terms_partner_name">{applicationForm.brokerName}</span>
              ) : null}
            </div>
          ) : null}

          <div className="business-v2-step-header fv_ubo_terms_onboarding_intro">
            <p className="business-v2-step-copy">
              You are opening an account with FV Bank International. Your broker prepared this application.
              Please review and accept the documents below and sign to continue to identity verification.
            </p>
          </div>

          <IndividualManagedAccountTerms
            data={{ ...(uboData || {}), customerSign: individualSign }}
            acceptedDocs={individualAcceptedDocs}
            onToggleDoc={handleToggleIndividualTncDoc}
            onSignatureChange={setIndividualSign}
            onSubmit={submitIndividualTerms}
            loading={savingIndividualTerms}
            showError={individualTermsShowError}
          />
          {termsError ? <div className="fv_ubo_terms_error">{termsError}</div> : null}
        </div>
      </FormContainerV2>
    );
  }

  if (needsTermsAcceptance) {
    return (
      <FormContainerV2
        form_id={applicationForm?._id || businessData?._id}
        title="Terms & Certificate of Beneficial Owners"
        isLoading={false}
        applicationForm={null}
        formData={businessData}
        hideLogout
        hideLogo={Boolean(brokerLogoUrl || applicationForm?.brokerName)}
      >
        <div className="form-content-body-content flex-1 fv_ubo_terms_onboarding_content">
          {brokerLogoUrl || applicationForm?.brokerName ? (
            <div className="fv_ubo_terms_partner">
              <img src={FvBankLogo} alt="FV Bank" className="fv_ubo_terms_partner_fv" />
              <span className="fv_ubo_terms_partner_x" aria-hidden="true">×</span>
              {brokerLogoUrl ? (
                <img
                  src={brokerLogoUrl}
                  alt={applicationForm.brokerName || "Broker"}
                  className="fv_ubo_terms_partner_logo"
                  onError={(event) => { event.currentTarget.style.display = "none"; }}
                />
              ) : null}
              {applicationForm?.brokerName ? (
                <span className="fv_ubo_terms_partner_name">{applicationForm.brokerName}</span>
              ) : null}
            </div>
          ) : null}

          <div className="business-v2-step-header fv_ubo_terms_onboarding_intro">
            <p className="business-v2-step-copy">
              You are opening an account with FV Bank International. Your broker is helping prepare the application and,
              once approved under your agreement with them, may submit instructions connected to this managed account.
              Please review and accept the items below yourself before identity verification.
            </p>
          </div>

          {getTermsAutoSaveMessage(termsAutoSaveStatus) ? (
            <div
              className={`fv_ubo_terms_autosave fv_ubo_terms_autosave--${termsAutoSaveStatus}`}
              role="status"
            >
              {getTermsAutoSaveMessage(termsAutoSaveStatus)}
            </div>
          ) : null}

          <SelfDeclarationV2
            data={businessData || {}}
            handleUpdate={submitTerms}
            hasError={termsHasError}
            setHasError={setTermsHasError}
            loading={savingTerms}
            cyclosFields={businessFields}
            errors={termsErrors}
            setErrors={setTermsErrors}
            handleChange={handleBusinessTermsChange}
            applicationForm={{
              ...(applicationForm || {}),
              type: businessData?.type || applicationForm?.type || V2_TYPE.PLUS,
            }}
            formId={applicationForm?._id || businessData?._id}
            wizard={WIZARD.SelfDeclaration}
            pageNum={WIZARD_NUM.SelfDeclaration}
            hideBackButton={brokerCompleteApplication}
            emitFieldChangeDetails={brokerCompleteApplication}
            managedAccountTncDocuments={MANAGED_ACCOUNT_TNC_DOCS}
          />
          {termsError ? <div className="fv_ubo_terms_error">{termsError}</div> : null}
        </div>
      </FormContainerV2>
    );
  }

  // Broker "complete-application" customers never see the "Verify Your Details"
  // (mobile phone) step — the broker already captured those details, so this
  // route goes straight from the Terms acceptance to KYC. The /complete-application
  // route is broker-only, so gate on the prop alone (not the async-loaded
  // brokerId, which can be momentarily stale right after the step flips).
  if (step === "verify" && !brokerCompleteApplication) {
    return (
      <PreUboDashboard
        uboData={uboData}
        applicationForm={applicationForm}
        usernameOnly={usernameOnly}
        onSuccess={handleVerificationSuccess}  // Pass success callback to proceed to the main dashboard
      />
    );
  }

  if (brokerCompleteApplication) {
    const statusName = getStatusName(ondatoData?.flowStatus);
    const addressParts = [
      uboData?.address?.addressLine ?? uboData?.address?.buildingNumber,
      uboData?.address?.addressLine2 ?? uboData?.address?.street,
      uboData?.address?.city,
      uboData?.address?.state,
      uboData?.address?.country,
    ].filter(Boolean);
    const formattedAddress = [
      addressParts.join(", "),
      uboData?.address?.pinCode,
    ].filter(Boolean).join(" - ");
    const shouldHideKycAction = [1, 3, 4, 5]?.includes(ondatoData?.flowStatus);

    return (
      <FormContainerV2
        form_id={applicationForm?._id || uboData?.application_id}
        title="Individual Verification"
        isLoading={false}
        applicationForm={null}
        formData={uboData}
        hideLogout
        hideLogo={Boolean(brokerLogoUrl || applicationForm?.brokerName)}
      >
        <div className="form-content-body-content flex-1 fv_ubo_kyc_onboarding_content">
          {brokerLogoUrl || applicationForm?.brokerName ? (
            <div className="fv_ubo_terms_partner">
              <img src={FvBankLogo} alt="FV Bank" className="fv_ubo_terms_partner_fv" />
              <span className="fv_ubo_terms_partner_x" aria-hidden="true">×</span>
              {brokerLogoUrl ? (
                <img
                  src={brokerLogoUrl}
                  alt={applicationForm.brokerName || "Broker"}
                  className="fv_ubo_terms_partner_logo"
                  onError={(event) => { event.currentTarget.style.display = "none"; }}
                />
              ) : null}
              {applicationForm?.brokerName ? (
                <span className="fv_ubo_terms_partner_name">{applicationForm.brokerName}</span>
              ) : null}
            </div>
          ) : null}

          <div className="business-v2-step-header fv_ubo_kyc_onboarding_intro">
            <h2 className="business-v2-step-title">
              Welcome, {uboData?.firstName} {uboData?.lastName}
            </h2>
          </div>

          {uboData ? (
            <div className="fv_ubo_kyc_details">
              {!showQRCode ? (
                <>
                  <div className="fv_ubo_kyc_detail">
                    <span className="fv_ubo_kyc_label">Status</span>
                    <span
                      className="fv_ubo_kyc_status"
                      style={{
                        borderColor: getColorForStatusViaString(statusName),
                        color: getColorForStatusViaString(statusName),
                        backgroundColor: getBackgroundColorForStatusViaString(statusName),
                      }}
                    >
                      {statusName}
                    </span>
                  </div>
                  <div className="fv_ubo_kyc_detail">
                    <span className="fv_ubo_kyc_label">First Name</span>
                    <span className="fv_ubo_kyc_value">{uboData.firstName}</span>
                  </div>
                  <div className="fv_ubo_kyc_detail">
                    <span className="fv_ubo_kyc_label">Last Name</span>
                    <span className="fv_ubo_kyc_value">{uboData.lastName}</span>
                  </div>
                  <div className="fv_ubo_kyc_detail">
                    <span className="fv_ubo_kyc_label">Email</span>
                    <span className="fv_ubo_kyc_value">{uboData.email}</span>
                  </div>
                  {uboData.phone ? (
                    <div className="fv_ubo_kyc_detail">
                      <span className="fv_ubo_kyc_label">Phone</span>
                      <span className="fv_ubo_kyc_value">{uboData.phone}</span>
                    </div>
                  ) : null}
                  {formattedAddress ? (
                    <div className="fv_ubo_kyc_detail">
                      <span className="fv_ubo_kyc_label">Address</span>
                      <span className="fv_ubo_kyc_value">{formattedAddress}</span>
                    </div>
                  ) : null}
                  {uboData.role ? (
                    <div className="fv_ubo_kyc_detail">
                      <span className="fv_ubo_kyc_label">Role</span>
                      <span className="fv_ubo_kyc_value">{uboData.role}</span>
                    </div>
                  ) : null}
                  {uboData.accessType ? (
                    <div className="fv_ubo_kyc_detail">
                      <span className="fv_ubo_kyc_label">Access Type</span>
                      <span className="fv_ubo_kyc_value">{uboData.accessType}</span>
                    </div>
                  ) : null}
                </>
              ) : null}

              <div className={`fv_ubo_kyc_actions ${showQRCode ? "is-centered" : ""}`}>
                {shouldHideKycAction ? null : (
                  <OndatoVerification
                    formId={uboData?.application_id}
                    uboId={uboData?._id}
                    onComplete={handleVerifyKYC}
                    onShowQRCode={handleShowQRCode}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </FormContainerV2>
    );
  }


  return (
    <div className={getContainerClassName()}>
      {brokerLogoUrl ? (
        <div className="fv_ubo_brand" style={{ marginBottom: "30px" }}>
          <img
            src={brokerLogoUrl}
            alt={applicationForm.brokerName || "Broker"}
            height="50"
            className="logo logo-dark fv_ubo_brand_logo"
            onError={(event) => { event.currentTarget.src = Logo; }}
          />
          {applicationForm.brokerName ? (
            <span className="fv_ubo_brand_name">{applicationForm.brokerName}</span>
          ) : null}
        </div>
      ) : (
        <img
          src={Logo}
          alt="fv-bank"
          height="50"
          style={{ marginBottom: "30px" }}
          className="logo logo-dark"
        />
      )}

      <h1 className="fv_ubo_title">Welcome, {uboData?.firstName} {uboData?.lastName}</h1>
      {/* <p className="fv_ubo_subtitle">Thank you for applying to FV Bank</p> */}
      {uboData && (
        <div className="fv_ubo_details">
          {!showQRCode && (
            <>

              <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Status:</span>
                <span className="fv_ubo_value">
                  <div
                    className='px-3'
                    style={{
                      borderRadius: "3px ",
                      fontSize: "12px",

                      border: `1px solid ${getColorForStatusViaString(getStatusName(ondatoData?.flowStatus))}`,
                      color: `${getColorForStatusViaString(getStatusName(ondatoData?.flowStatus))}`,
                      backgroundColor: `${getBackgroundColorForStatusViaString(getStatusName(ondatoData?.flowStatus))}`,
                    }}
                  >
                    {getStatusName(ondatoData?.flowStatus)}
                  </div>
                </span>
              </div>

              <div className="fv_ubo_detail">
                <span className="fv_ubo_label">First Name:</span>
                <span className="fv_ubo_value">{uboData.firstName}</span>
              </div>
              <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Last Name:</span>
                <span className="fv_ubo_value">{uboData.lastName}</span>
              </div>
              <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Email:</span>
                <span className="fv_ubo_value">{uboData.email}</span>
              </div>
              {uboData.phone ? <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Phone:</span>
                <span className="fv_ubo_value">{uboData.phone}</span>
              </div> : null}
              <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Address:</span>
                <span className="fv_ubo_value">
                  {uboData.address.addressLine ?? uboData.address.buildingNumber}, {uboData.address.addressLine2 ?? uboData.address.street}, {uboData.address.city}, {uboData.address.state}, {uboData.address.country} - {uboData.address.pinCode}
                </span>
              </div>
              {uboData.role ? <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Role:</span>
                <span className="fv_ubo_value">{uboData.role}</span>
              </div> : null}
              {uboData.accessType ? <div className="fv_ubo_detail">
                <span className="fv_ubo_label">Access Type:</span>
                <span className="fv_ubo_value">{uboData.accessType}</span>
              </div> : null}




            </>
          )}
          <div className={`w-100 justify-content-${showQRCode ? "center" : "end"} d-flex`}>
            {
              [1,3,4,5]?.includes(ondatoData?.flowStatus)? <div> </div> :
                <OndatoVerification
                  formId={uboData?.application_id}
                  uboId={uboData?._id}
                  onComplete={handleVerifyKYC}
                  onShowQRCode={handleShowQRCode}
                />
            }
          </div>
        </div>
      )}

    </div>
  );
};

export default UboDashboard;
